// src/pages/Home.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import apiService from '../services/api';
import StorageService from '../utils/storage';
import tmdbService from '../services/tmdb';
import LazyImage from '../components/LazyImage';
import MovieDetailsModal from '../components/MovieDetailsModal';
import SeriesDetailsModal from '../components/SeriesDetailsModal';
import MainLayout from '../components/MainLayout';

const Content = styled.div`
  padding: 40px;
  width: 100%;
  min-height: 100vh;
  position: relative;
  overflow-y: auto;
`;

const Header = styled.header`
  position: fixed;
  top: 0;
  right: 0;
  left: 240px;
  z-index: 100;
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(to bottom, rgba(26, 16, 51, 0.9) 0%, rgba(26, 16, 51, 0) 100%);
  transition: all 0.3s ease;
  
  &.scrolled {
    background: rgba(26, 16, 51, 0.9);
    backdrop-filter: blur(10px);
  }
`;

const Navigation = styled.nav`
  display: flex;
  gap: 20px;
`;

const NavLink = styled(Link)`
  color: #fff;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  opacity: 0.8;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 1;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  width: 300px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 40px 10px 15px;
  background-color: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 4px;
  color: white;
  font-size: 14px;
  
  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.5);
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-left: 20px;
  align-items: center;
`;

const FilterButton = styled.button`
  background: ${props => props.$active ? props.$color : 'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$active ? '#fff' : '#aaa'};
  border: 1px solid ${props => props.$active ? props.$color : 'rgba(255, 255, 255, 0.1)'};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.$active ? props.$color : 'rgba(255, 255, 255, 0.2)'};
    color: #fff;
  }
`;

const ContentSection = styled.div`
  padding: 40px;
  padding-top: 100px;
  width: 100%;
  position: relative;
`;

const SectionTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin: 0 0 20px;
  color: #fff;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 40px;
`;

const CarouselContainer = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 20px;
  padding: 0 40px;
  height: 180px;
`;

const CarouselTrack = styled.div`
  display: flex;
  gap: 10px;
  overflow-x: auto;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 10px 0;
  height: 100%;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CarouselButton = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(43, 27, 84, 0.9);
  border: none;
  border-radius: 50%;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  z-index: 2;
  transition: all 0.3s ease;
  backdrop-filter: blur(4px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: rgba(57, 38, 103, 0.95);
    transform: translateY(-50%) scale(1.1);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
  }
  
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
    transform: translateY(-50%) scale(1);
  }
  
  &.prev {
    left: -10px;
  }
  
  &.next {
    right: -10px;
  }
  
  svg {
    width: 28px;
    height: 28px;
    filter: drop-shadow(0 2px 3px rgba(0, 0, 0, 0.2));
  }
`;

const CarouselItem = styled.div`
  flex: 0 0 120px;
  width: 120px;
  height: 160px;
  
  a {
    height: 100%;
  }
`;

const ChannelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 0 10px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ContentCard = styled(Link)`
  position: relative;
  aspect-ratio: ${props => props.$inCarousel ? '2/3' : '2/3'};
  height: ${props => props.$inCarousel ? '100%' : 'auto'};
  border-radius: 4px;
  overflow: hidden;
  background-color: rgba(26, 16, 51, 0.5);
  transition: all 0.3s ease;
  text-decoration: none;
  
  &:hover {
    transform: ${props => props.$inCarousel ? 'scale(1.1)' : 'scale(1.05)'};
    z-index: 1;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
    
    .content-info {
      opacity: 1;
    }
  }
`;

const ChannelCard = styled(Link)`
  position: relative;
  background: rgba(26, 16, 51, 0.5);
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  text-decoration: none;
  display: flex;
  align-items: center;
  padding: 15px;
  gap: 20px;
  
  &:hover {
    transform: translateY(-2px);
    background: rgba(43, 27, 84, 0.8);
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.3);
  }
`;

const ChannelLogo = styled.div`
  width: 80px;
  height: 80px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  overflow: hidden;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .placeholder {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background-color: #E50914;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
    font-weight: bold;
  }
`;

const ChannelInfo = styled.div`
  flex: 1;
  min-width: 0; // Importante para permitir que o texto seja truncado
`;

const ChannelName = styled.div`
  color: white;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChannelGroup = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  margin-bottom: 5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChannelQuality = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
  background-color: ${props => {
    switch (props.$quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  }};
  color: white;
  font-size: 12px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ContentPoster = styled.div`
  width: 100%;
  height: 100%;
  background-size: cover;
  background-position: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.8) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  ${ContentCard}:hover &::before {
    opacity: 1;
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const ContentInfo = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  color: white;
  z-index: 1;
  
  ${ContentCard}:hover & {
    opacity: 1;
  }
`;

const ContentTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const ContentMeta = styled.div`
  font-size: 12px;
  color: #ccc;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const QualityBadge = styled.span`
  background-color: ${props => {
    switch (props.$quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  }};
  color: white;
  font-size: 12px;
  font-weight: bold;
  padding: 2px 6px;
  border-radius: 2px;
`;

const ContentDescription = styled.div`
  font-size: 12px;
  color: #ccc;
  margin-top: 10px;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100%;
  position: fixed;
  top: 0;
  left: 0;
  background: linear-gradient(135deg, #1a1033 0%, #2b1b54 50%, #392667 100%);
  z-index: 9999;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  border-top-color: #E50914;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: #fff;
  font-size: 18px;
  margin-bottom: 20px;
  text-align: center;
`;

const ProgressBar = styled.div`
  width: 300px;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 10px;
`;

const ProgressFill = styled.div`
  width: ${props => props.$percent}%;
  height: 100%;
  background: #E50914;
  transition: width 0.3s ease;
`;

const ProgressText = styled.div`
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
  text-align: center;
`;

const LoadMoreButton = styled.button`
  background: #E50914;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  cursor: pointer;
  margin: 20px auto;
  display: block;
  font-size: 16px;
  
  &:hover {
    background: #F40D17;
  }
  
  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

// Adicionar componentes para o indicador de progresso
const SeriesProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 2px;
  margin-top: 8px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: ${props => props.$progress || 0}%;
    background: #E50914;
    border-radius: 2px;
    transition: width 0.3s ease;
  }
`;

const WatchedIndicator = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4caf50;
  z-index: 2;
`;

const Home = () => {
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(() => {
    const savedCategory = localStorage.getItem('selectedCategory');
    return savedCategory || '';
  });
  const [selectedType, setSelectedType] = useState(() => {
    const savedType = localStorage.getItem('selectedType');
    return savedType || 'all';
  });
  const [activeQualities, setActiveQualities] = useState(() => {
    const saved = localStorage.getItem('activeQualities');
    return saved ? JSON.parse(saved) : ['SD', 'HD', 'FHD', '4K'];
  });
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  const [content, setContent] = useState({
    channels: [],
    movies: [],
    series: [],
    categories: new Set(),
    recentlyWatched: [],
    featured: null
  });

  const [loadingProgress, setLoadingProgress] = useState({
    type: '',
    percent: 0,
    message: ''
  });

  const [initialLoad, setInitialLoad] = useState(true);

  // Configurações de paginação
  const [channelsPage, setChannelsPage] = useState(1);
  const [moviesPage, setMoviesPage] = useState(1);
  const [seriesPage, setSeriesPage] = useState(1);
  const ITEMS_PER_PAGE = 100; // Aumentado de 50 para 100 para mostrar mais itens por página

  const channelsLoadingRef = useRef(null);
  const moviesLoadingRef = useRef(null);
  const seriesLoadingRef = useRef(null);

  const navigate = useNavigate();

  const contentSectionRef = useRef(null);

  const carouselRef = useRef(null);

  const [selectedContent, setSelectedContent] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Novos estados para a modal de séries
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [isSeriesModalOpen, setIsSeriesModalOpen] = useState(false);

  // Função para detectar a qualidade
  const detectQuality = (name) => {
    if (!name) return 'SD';

    const nameLower = name.toLowerCase();

    // Detecta qualidades compostas primeiro (ordem é importante)
    if (nameLower.includes('4k') && nameLower.includes('hdr')) return '4K HDR';
    if (nameLower.includes('4k') && nameLower.includes('h265')) return '4K H265';
    if (nameLower.includes('4k') && nameLower.includes('hevc')) return '4K H265';
    if (nameLower.includes('uhd') && nameLower.includes('hdr')) return '4K HDR';
    if (nameLower.includes('uhd') && nameLower.includes('h265')) return '4K H265';
    if (nameLower.includes('uhd') && nameLower.includes('hevc')) return '4K H265';
    if (nameLower.includes('fhd') && nameLower.includes('hdr')) return 'FHD HDR';
    if (nameLower.includes('fhd') && nameLower.includes('h265')) return 'FHD H265';
    if (nameLower.includes('fhd') && nameLower.includes('hevc')) return 'FHD H265';
    if (nameLower.includes('1080') && nameLower.includes('hdr')) return 'FHD HDR';
    if (nameLower.includes('1080') && nameLower.includes('h265')) return 'FHD H265';
    if (nameLower.includes('1080') && nameLower.includes('hevc')) return 'FHD H265';

    // Depois verifica qualidades simples
    if (nameLower.includes('4k') || nameLower.includes('uhd')) return '4K';
    if (nameLower.includes('fhd') || nameLower.includes('fullhd') ||
      nameLower.includes('full hd') || nameLower.includes('1080')) return 'FHD';
    if (nameLower.includes('hd') || nameLower.includes('720')) return 'HD';

    return 'SD';
  };

  // Função para processar o conteúdo
  const processContent = (data) => {
    const processed = {
      channels: [],
      movies: [],
      series: [],
      categories: new Set()
    };

    for (const item of data) {
      const groupTitle = item.group || '';
      processed.categories.add(groupTitle);

      // Usa o nome limpo se disponível, ou o nome original
      const displayName = item.cleanName || item.name;

      // Verificar se é um canal de filmes 24h específico ou um dos canais especiais
      const isSpecificMovieChannel =
        groupTitle.toLowerCase().includes('filmes e séries') ||
        groupTitle.toLowerCase().includes('filmes selecionados') ||
        (groupTitle.toLowerCase().includes('filmes') &&
          (groupTitle.toLowerCase().includes('24h') ||
            groupTitle.toLowerCase().includes('canais')));

      // Verificar se o próprio nome do canal contém "filmes e séries" ou "filmes selecionados"
      const isSpecialChannel =
        displayName.toLowerCase().includes('filmes e séries') ||
        displayName.toLowerCase().includes('filmes selecionados');

      // Adiciona o item ao grupo apropriado
      if (isSpecificMovieChannel || isSpecialChannel) {
        // Estes são canais de filmes 24h ou canais especiais, devem ir para channels
        processed.channels.push({
          ...item,
          displayName
        });
      } else if (groupTitle.toLowerCase().includes('series')) {
        const seriesInfo = parseSeriesInfo(item);
        processed.series.push({
          ...item,
          displayName,
          ...seriesInfo
        });
      } else if (groupTitle.toLowerCase().includes('filmes')) {
        processed.movies.push({
          ...item,
          displayName
        });
      } else {
        processed.channels.push({
          ...item,
          displayName
        });
      }
    }

    return processed;
  };

  // Função para extrair informações de séries
  const parseSeriesInfo = (item) => {
    const name = item.name || '';
    const regex = /(.+?)(?:\s+S(\d+)E(\d+))?(?:\s+\[([^\]]+)\])?$/i;
    const matches = name.match(regex);

    if (matches) {
      return {
        title: matches[1]?.trim(),
        season: matches[2] ? parseInt(matches[2]) : null,
        episode: matches[3] ? parseInt(matches[3]) : null,
        language: matches[4] || null
      };
    }

    return {
      title: name,
      season: null,
      episode: null,
      language: null
    };
  };

  // Handler para eventos de progresso
  const handleProgress = useCallback(({ event, data }) => {
    switch (event) {
      case 'downloadStart':
        setLoadingProgress({
          type: 'download',
          percent: 0,
          message: 'Baixando lista de canais...'
        });
        break;
      case 'downloadProgress':
        setLoadingProgress({
          type: 'download',
          percent: data.percent,
          message: `Baixando lista de canais: ${data.percent}%`
        });
        break;
      case 'processingStart':
        setLoadingProgress({
          type: 'processing',
          percent: 0,
          message: 'Processando canais...'
        });
        break;
      case 'processingProgress':
        setLoadingProgress({
          type: 'processing',
          percent: data.percent,
          message: `Processando canais: ${data.percent}% (${data.totalChannels} canais)`
        });
        break;
      case 'processingComplete':
        setLoadingProgress({
          type: 'complete',
          percent: 100,
          message: `Processamento concluído: ${data.totalChannels} canais em ${data.totalChunks} chunks`
        });
        break;
      case 'error':
        setLoadingProgress({
          type: 'error',
          percent: 0,
          message: 'Erro ao carregar canais'
        });
        break;
      default:
        break;
    }
  }, []);

  // Carregar canais
  useEffect(() => {
    const loadChannels = async () => {
      try {
        setLoading(true);

        // Verificar se já temos canais em cache no apiService
        if (apiService.isLoaded) {
          console.log('Canais já carregados na memória, usando cache');

          // Processar os canais já carregados
          const allChannels = [...apiService.channels];
          console.log(`Total de canais carregados: ${allChannels.length}`);

          // Processa todos os canais
          const processed = processContent(allChannels);

          // Obter conteúdo recente
          const recentlyWatched = StorageService.getRecentChannels();

          setContent({
            channels: processed.channels || [],
            movies: processed.movies || [],
            series: processed.series || [],
            categories: processed.categories || new Set(),
            recentlyWatched: recentlyWatched || []
          });

          setLoading(false);
          setInitialLoad(false);
          return;
        }

        // Se não temos canais em cache, carregar da API
        apiService.addProgressListener(handleProgress);

        const loadedChannels = await apiService.loadChannels();

        // Processar os canais carregados
        const processed = processContent(loadedChannels.channels || loadedChannels);

        // Obter conteúdo recente
        const recentlyWatched = StorageService.getRecentChannels();

        setContent({
          channels: processed.channels || [],
          movies: processed.movies || [],
          series: processed.series || [],
          categories: processed.categories || new Set(),
          recentlyWatched: recentlyWatched || []
        });

      } catch (error) {
        console.error('Erro ao carregar canais:', error);
        setLoadingProgress({
          type: 'error',
          percent: 0,
          message: 'Erro ao carregar canais. Por favor, tente novamente mais tarde.'
        });
      } finally {
        setLoading(false);
        setInitialLoad(false);
        apiService.removeProgressListener(handleProgress);
      }
    };

    loadChannels();

    const handleScroll = () => {
      setIsHeaderScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleProgress]);

  // Atualizar localStorage quando activeQualities mudar
  useEffect(() => {
    localStorage.setItem('activeQualities', JSON.stringify(activeQualities));
  }, [activeQualities]);

  const toggleQuality = (quality) => {
    setActiveQualities(prev => {
      if (prev.includes(quality)) {
        // Não permitir desmarcar todos
        if (prev.length === 1) return prev;
        return prev.filter(q => q !== quality);
      } else {
        return [...prev, quality];
      }
    });
  };

  const getQualityCategory = (qualityString) => {
    if (!qualityString) return 'SD';
    const q = qualityString.toUpperCase();
    if (q.includes('4K') || q.includes('UHD')) return '4K';
    if (q.includes('FHD') || q.includes('1080')) return 'FHD';
    if (q.includes('HD') || q.includes('720')) return 'HD';
    return 'SD';
  };

  // Filtrar conteúdo baseado na busca e categoria
  const filterContent = (items) => {
    // Garantir que items seja um array
    if (!Array.isArray(items)) {
      return [];
    }

    // Se não há filtros ativos, retorna todos os itens sem limitação
    if (searchQuery === '' && selectedCategory === '') {
      return items;
    }

    // Filtra todos os itens sem limitação de quantidade
    return items.filter(item => {
      const matchesSearch = searchQuery === '' ||
        (item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === '' ||
        item.group === selectedCategory;

      // Filtro de qualidade
      const itemQuality = item.quality || detectQuality(item.name);
      const qualityCategory = getQualityCategory(itemQuality);
      const matchesQuality = activeQualities.includes(qualityCategory);

      return matchesSearch && matchesCategory && matchesQuality;
    });
  };

  // Agrupar séries por título
  const groupSeries = (series) => {
    // Garantir que series seja um array
    if (!Array.isArray(series)) {
      return {};
    }

    // Processa todas as séries sem limitação
    const grouped = {};
    series.forEach(episode => {
      if (!episode.title) return; // Pula episódios sem título

      if (!grouped[episode.title]) {
        grouped[episode.title] = {
          title: episode.title,
          seasons: {},
          logo: episode.logo, // Preserva o logo para exibição
          tmdbData: null // Adicionar campo para dados do TMDB
        };
      }

      if (episode.season) {
        if (!grouped[episode.title].seasons[episode.season]) {
          grouped[episode.title].seasons[episode.season] = [];
        }
        grouped[episode.title].seasons[episode.season].push(episode);
      }
    });

    return grouped;
  };

  // Função para renderizar um card de conteúdo
  const renderContentCard = (item, inCarousel = false) => {
    const displayName = item.displayName || item.cleanName || item.name;

    return (
      <ContentCard
        key={`${item.id}-${item.url}`}
        onClick={(e) => {
          e.preventDefault();
          setSelectedContent(item);
          setIsModalOpen(true);
        }}
        to={`/watch/${encodeURIComponent(item.id)}`}
        $inCarousel={inCarousel}
      >
        <ContentPoster>
          <LazyImage
            src={item.logo || '/placeholder.png'}
            alt={displayName}
            onError={(e) => {
              if (e.target.src !== '/placeholder.png') {
                e.target.src = '/placeholder.png';
              }
            }}
          />
        </ContentPoster>
        <ContentInfo>
          <ContentTitle>{displayName}</ContentTitle>
          <ContentMeta>
            <QualityBadge $quality={item.quality || detectQuality(item.name)}>
              {item.quality || detectQuality(item.name)}
            </QualityBadge>
            <span>{item.group}</span>
          </ContentMeta>
        </ContentInfo>
      </ContentCard>
    );
  };

  // Função para renderizar um card de canal
  const renderChannelCard = (channel, inCarousel = false) => {
    if (inCarousel) {
      // Mantém o layout original para o carrossel
      return (
        <ChannelCard key={`${channel.id}-${channel.url}`} to={`/watch/${encodeURIComponent(channel.id)}`} $inCarousel={true}>
          <ChannelQuality $quality={channel.quality || detectQuality(channel.name)}>
            {channel.quality || detectQuality(channel.name)}
          </ChannelQuality>
          <ChannelLogo>
            <LazyImage
              src={channel.logo || '/placeholder.png'}
              alt={channel.displayName || channel.cleanName || channel.name}
              onError={(e) => {
                if (e.target.src !== '/placeholder.png') {
                  e.target.src = '/placeholder.png';
                }
              }}
            />
          </ChannelLogo>
          <ChannelName>{channel.displayName || channel.cleanName || channel.name}</ChannelName>
        </ChannelCard>
      );
    }

    const displayName = channel.displayName || channel.cleanName || channel.name;
    const quality = channel.quality || detectQuality(channel.name);

    return (
      <ChannelCard key={`${channel.id}-${channel.url}`} to={`/watch/${encodeURIComponent(channel.id)}`}>
        <ChannelLogo>
          <LazyImage
            src={channel.logo || '/placeholder.png'}
            alt={displayName}
            onError={(e) => {
              if (e.target.src !== '/placeholder.png') {
                e.target.src = '/placeholder.png';
              }
            }}
          />
        </ChannelLogo>
        <ChannelInfo>
          <ChannelName>{displayName}</ChannelName>
          <ChannelGroup>{channel.group}</ChannelGroup>
        </ChannelInfo>
        <ChannelQuality $quality={quality}>
          {quality}
        </ChannelQuality>
      </ChannelCard>
    );
  };

  // Função para carregar mais itens
  const loadMoreItems = (type) => {
    if (loading) return;

    switch (type) {
      case 'channels':
        setChannelsPage(prev => prev + 1);
        break;
      case 'movies':
        setMoviesPage(prev => prev + 1);
        break;
      case 'series':
        setSeriesPage(prev => prev + 1);
        break;
      default:
        break;
    }
  };

  // Configurar os observadores de interseção para carregamento automático
  useEffect(() => {
    const setupObserver = (ref, type) => {
      if (ref.current) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !loading) {
              loadMoreItems(type);
            }
          },
          { threshold: 0.1 }
        );

        observer.observe(ref.current);
        return observer;
      }
      return null;
    };

    const channelsObserver = setupObserver(channelsLoadingRef, 'channels');
    const moviesObserver = setupObserver(moviesLoadingRef, 'movies');
    const seriesObserver = setupObserver(seriesLoadingRef, 'series');

    return () => {
      if (channelsObserver) channelsObserver.disconnect();
      if (moviesObserver) moviesObserver.disconnect();
      if (seriesObserver) seriesObserver.disconnect();
    };
  }, [loading, loadMoreItems]);

  // Função para paginar os resultados
  const paginateItems = (items, page, itemsPerPage) => {
    // Garantir que items seja um array
    const itemsArray = Array.isArray(items) ? items : [];
    return itemsArray.slice(0, page * itemsPerPage);
  };

  // Função para fazer scroll até a seção de conteúdo
  const scrollToContent = () => {
    if (contentSectionRef.current) {
      const yOffset = -80; // Ajuste para compensar o header fixo
      const y = contentSectionRef.current.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  // Atualizar os handlers de categoria para incluir a persistência
  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    localStorage.setItem('selectedCategory', category);
    scrollToContent();
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    localStorage.setItem('selectedType', type);
    // Limpar a categoria ao mudar o tipo
    setSelectedCategory('');
    localStorage.setItem('selectedCategory', '');
    scrollToContent();
  };

  const handlePrevClick = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: -220, behavior: 'smooth' });
    }
  };

  const handleNextClick = () => {
    if (carouselRef.current) {
      carouselRef.current.scrollBy({ left: 220, behavior: 'smooth' });
    }
  };

  // Adicionar um efeito para pré-carregar metadados do TMDB para séries populares
  useEffect(() => {
    const preloadTMDBData = async () => {
      if (loading || !content.series.length) return;

      // Agrupar séries
      const groupedSeries = groupSeries(content.series);
      const seriesList = Object.values(groupedSeries);

      // Limitar a 20 séries mais populares para não sobrecarregar a API
      const popularSeries = seriesList.slice(0, 20);

      // Carregar dados do TMDB para cada série
      for (const series of popularSeries) {
        try {
          // Verificar cache primeiro
          const cacheKey = `tmdb_series_${series.title}`;
          const cachedData = localStorage.getItem(cacheKey);

          if (cachedData) {
            series.tmdbData = JSON.parse(cachedData);
            continue;
          }

          // Buscar dados do TMDB
          const metadata = await tmdbService.getMetadata(series.title, 'tv');
          if (metadata) {
            // Salvar no cache
            localStorage.setItem(cacheKey, JSON.stringify(metadata));
            series.tmdbData = metadata;
          }
        } catch (error) {
          console.error(`Erro ao carregar dados TMDB para ${series.title}:`, error);
        }
      }
    };

    preloadTMDBData();
  }, [loading, content.series]);

  // Função para calcular o progresso de uma série
  const calculateSeriesProgress = (seriesTitle) => {
    if (!seriesTitle) return { watched: 0, total: 0, percent: 0 };

    const storageKey = `watched_episodes_${seriesTitle}`;
    const savedWatched = localStorage.getItem(storageKey);

    if (!savedWatched) return { watched: 0, total: 0, percent: 0 };

    const watchedEpisodes = JSON.parse(savedWatched);
    const watchedCount = Object.keys(watchedEpisodes).length;

    // Como não temos o total de episódios facilmente disponível,
    // vamos usar uma estimativa baseada no número de episódios assistidos
    // Se o usuário assistiu pelo menos um episódio, consideramos que começou a série
    const hasStarted = watchedCount > 0;

    return {
      watched: watchedCount,
      hasStarted,
      percent: hasStarted ? Math.min(100, watchedCount * 5) : 0 // Estimativa visual
    };
  };

  if (loading && initialLoad) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>{loadingProgress.message || 'Iniciando carregamento...'}</LoadingText>
        <ProgressBar>
          <ProgressFill $percent={loadingProgress.percent || 0} />
        </ProgressBar>
        <ProgressText>
          {loadingProgress.percent ? `${loadingProgress.percent}%` : 'Preparando...'}
        </ProgressText>
      </LoadingContainer>
    );
  }

  // Ordenar categorias alfabeticamente
  const sortedCategories = Array.from(content.categories).sort((a, b) =>
    a.localeCompare(b, 'pt-BR')
  );

  // Filtrar e paginar os conteúdos
  const filteredChannels = filterContent(content.channels);
  const filteredMovies = filterContent(content.movies);
  const filteredSeries = filterContent(content.series);

  const paginatedChannels = paginateItems(filteredChannels, channelsPage, ITEMS_PER_PAGE);
  const paginatedMovies = paginateItems(filteredMovies, moviesPage, ITEMS_PER_PAGE);
  const paginatedSeries = paginateItems(filteredSeries, seriesPage, ITEMS_PER_PAGE);

  const hasMoreChannels = paginatedChannels.length < filteredChannels.length;
  const hasMoreMovies = paginatedMovies.length < filteredMovies.length;
  const hasMoreSeries = paginatedSeries.length < filteredSeries.length;

  return (
    <MainLayout
      categories={sortedCategories}
      selectedCategory={selectedCategory}
      selectedType={selectedType}
      onCategoryChange={handleCategoryChange}
      onTypeChange={handleTypeChange}
    >
      <Content>
        <Header className={isHeaderScrolled ? 'scrolled' : ''}>
          <SearchContainer>
            <SearchInput
              type="text"
              placeholder="Buscar..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <SearchIcon>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </SearchIcon>
          </SearchContainer>

          <FilterContainer>
            <FilterButton
              $active={activeQualities.includes('SD')}
              $color="#666"
              onClick={() => toggleQuality('SD')}
            >
              SD
            </FilterButton>
            <FilterButton
              $active={activeQualities.includes('HD')}
              $color="#4caf50"
              onClick={() => toggleQuality('HD')}
            >
              HD
            </FilterButton>
            <FilterButton
              $active={activeQualities.includes('FHD')}
              $color="#2196f3"
              onClick={() => toggleQuality('FHD')}
            >
              FHD
            </FilterButton>
            <FilterButton
              $active={activeQualities.includes('4K')}
              $color="#E50914"
              onClick={() => toggleQuality('4K')}
            >
              4K
            </FilterButton>
          </FilterContainer>

          <Navigation>
            <NavLink to="/favorites">Favoritos</NavLink>
            <NavLink to="/settings">Configurações</NavLink>
          </Navigation>
        </Header>

        <ContentSection ref={contentSectionRef}>
          {loading && initialLoad ? (
            <LoadingContainer>
              <LoadingText>Carregando canais... {loadingProgress.percent}%</LoadingText>
            </LoadingContainer>
          ) : (
            <>
              {content.recentlyWatched.length > 0 && (
                <>
                  <SectionTitle>Continuar Assistindo</SectionTitle>
                  <CarouselContainer>
                    <CarouselButton className="prev" onClick={handlePrevClick}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.5 19L8.5 12L15.5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </CarouselButton>
                    <CarouselTrack ref={carouselRef}>
                      {content.recentlyWatched.map(item => (
                        <CarouselItem key={`${item.id}-${item.url}`}>
                          {item.group?.toLowerCase().includes('canais')
                            ? renderChannelCard(item, true)
                            : renderContentCard(item, true)
                          }
                        </CarouselItem>
                      ))}
                    </CarouselTrack>
                    <CarouselButton className="next" onClick={handleNextClick}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M8.5 19L15.5 12L8.5 5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </CarouselButton>
                  </CarouselContainer>
                </>
              )}

              {(selectedType === 'all' || selectedType === 'channels') && (
                <>
                  <SectionTitle ref={contentSectionRef}>Canais</SectionTitle>
                  <ChannelGrid>
                    {paginatedChannels.map(renderChannelCard)}
                  </ChannelGrid>
                  {hasMoreChannels && (
                    <LoadMoreButton onClick={() => loadMoreItems('channels')}>
                      Carregar Mais Canais
                    </LoadMoreButton>
                  )}
                </>
              )}

              {(selectedType === 'all' || selectedType === 'movies') && (
                <>
                  <SectionTitle>Filmes</SectionTitle>
                  <ContentGrid>
                    {paginatedMovies.map(renderContentCard)}
                  </ContentGrid>
                  {hasMoreMovies && (
                    <LoadMoreButton onClick={() => loadMoreItems('movies')}>
                      Carregar Mais Filmes
                    </LoadMoreButton>
                  )}
                </>
              )}

              {(selectedType === 'all' || selectedType === 'series') && (
                <>
                  <SectionTitle>Séries</SectionTitle>
                  <ContentGrid>
                    {Object.values(groupSeries(paginatedSeries))
                      .sort((a, b) => a.title.localeCompare(b.title, 'pt-BR'))
                      .map(series => {
                        // Verificar se temos dados do TMDB em cache
                        const cacheKey = `tmdb_series_${series.title}`;
                        const cachedData = localStorage.getItem(cacheKey);
                        const tmdbData = cachedData ? JSON.parse(cachedData) : null;

                        // Usar dados do TMDB se disponíveis
                        const posterPath = tmdbData?.poster_path ?
                          tmdbService.getImageUrl(tmdbData.poster_path) :
                          series.logo;

                        const seasonCount = tmdbData?.number_of_seasons ||
                          Object.keys(series.seasons).length;

                        // Calcular progresso da série
                        const progress = calculateSeriesProgress(series.title);

                        return (
                          <ContentCard
                            key={series.title}
                            onClick={(e) => {
                              e.preventDefault();
                              console.log('Série selecionada:', series);
                              // Adicionar dados do TMDB à série selecionada
                              if (tmdbData) {
                                series.tmdbData = tmdbData;
                              }
                              setSelectedSeries(series);
                              setIsSeriesModalOpen(true);
                            }}
                          >
                            <ContentPoster>
                              {progress.hasStarted && (
                                <WatchedIndicator>
                                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="16" height="16">
                                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                                  </svg>
                                </WatchedIndicator>
                              )}
                              {posterPath ? (
                                <LazyImage
                                  src={posterPath}
                                  alt={series.title}
                                  placeholder={
                                    <div style={{
                                      width: '100%',
                                      height: '100%',
                                      background: `linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)`,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '32px',
                                      color: '#E50914'
                                    }}>
                                      {series.title.charAt(0)}
                                    </div>
                                  }
                                />
                              ) : (
                                <div style={{
                                  width: '100%',
                                  height: '100%',
                                  background: `linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%)`,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '32px',
                                  color: '#E50914'
                                }}>
                                  {series.title.charAt(0)}
                                </div>
                              )}
                            </ContentPoster>
                            <ContentInfo>
                              <ContentTitle>{series.title}</ContentTitle>
                              <ContentMeta>
                                <span>{seasonCount} Temporada(s)</span>
                              </ContentMeta>
                              {progress.hasStarted && (
                                <SeriesProgressBar $progress={progress.percent} />
                              )}
                            </ContentInfo>
                          </ContentCard>
                        );
                      })}
                  </ContentGrid>
                  {hasMoreSeries && (
                    <LoadMoreButton onClick={() => loadMoreItems('series')}>
                      Carregar Mais Séries
                    </LoadMoreButton>
                  )}
                </>
              )}
            </>
          )}
        </ContentSection>
      </Content>

      <MovieDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        content={selectedContent}
        onWatch={() => {
          if (selectedContent) {
            navigate(`/watch/${encodeURIComponent(selectedContent.id)}`);
          }
        }}
        isFavorite={selectedContent ? StorageService.isFavorite(selectedContent.id) : false}
        onToggleFavorite={() => {
          if (selectedContent) {
            if (StorageService.isFavorite(selectedContent.id)) {
              StorageService.removeFavorite(selectedContent.id);
            } else {
              StorageService.addFavorite(selectedContent);
            }
            // Força atualização do estado para re-renderizar o botão
            setSelectedContent({ ...selectedContent });
          }
        }}
      />

      <SeriesDetailsModal
        isOpen={isSeriesModalOpen}
        onClose={() => setIsSeriesModalOpen(false)}
        seriesData={selectedSeries}
        onWatchEpisode={(episode) => {
          if (episode) {
            console.log('Assistindo episódio:', episode);

            // Marcar episódio como assistido automaticamente
            if (selectedSeries && selectedSeries.title && episode.season && episode.episode) {
              const episodeKey = `S${episode.season}E${episode.episode}`;
              const storageKey = `watched_episodes_${selectedSeries.title}`;

              // Obter episódios assistidos atuais
              const savedWatched = localStorage.getItem(storageKey);
              const watchedEpisodes = savedWatched ? JSON.parse(savedWatched) : {};

              // Marcar este episódio como assistido
              watchedEpisodes[episodeKey] = true;

              // Salvar de volta no localStorage
              localStorage.setItem(storageKey, JSON.stringify(watchedEpisodes));
            }

            navigate(`/watch/${encodeURIComponent(episode.id)}`);
          }
        }}
        isFavorite={selectedSeries ? StorageService.isFavorite(selectedSeries.title) : false}
        onToggleFavorite={() => {
          if (selectedSeries) {
            if (StorageService.isFavorite(selectedSeries.title)) {
              StorageService.removeFavorite(selectedSeries.title);
            } else {
              // Adicionar a série como favorito (usando o título como ID)
              StorageService.addFavorite({
                id: selectedSeries.title,
                name: selectedSeries.title,
                logo: selectedSeries.logo,
                group: 'Series'
              });
            }
            // Força atualização do estado para re-renderizar o botão
            setSelectedSeries({ ...selectedSeries });
          }
        }}
      />
    </MainLayout>
  );
};

export default Home;